import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { supabase } from '../data/supabaseClient';
import { CustomButton2 } from '../Templates';
import Spinner from '../common/Spinner';
import { logProfileError, logProfileAction, logProfilePerformance } from '../../utils/errorLogger';
import { getCountryOptions } from '../../utils/countryUtils';
import { FEATURES } from '../../config/features';
import AddFavoriteLocationInline from './AddFavoriteLocationInline';
import './ProfileComplete.css';

// Username validation constants
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 10;
const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;

// Language detection function
const detectUserLanguage = () => {
  // Supported languages in your app
  const supportedLanguages = ['en', 'lv', 'ru'];
  
  // Try browser language first
  const browserLang = navigator.language || navigator.userLanguage;
  const primaryLang = browserLang ? browserLang.split('-')[0].toLowerCase() : null;
  
  // Check if browser language is supported
  if (primaryLang && supportedLanguages.includes(primaryLang)) {
    return primaryLang;
  }
  
  // Try browser languages array
  if (navigator.languages) {
    for (const lang of navigator.languages) {
      const langCode = lang.split('-')[0].toLowerCase();
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }
  }
  
  // Check for regional variations (like en-US, en-GB)
  if (browserLang) {
    if (browserLang.toLowerCase().startsWith('en')) return 'en';
    if (browserLang.toLowerCase().startsWith('lv')) return 'lv';
    if (browserLang.toLowerCase().startsWith('ru')) return 'ru';
  }
  
  // Fallback to English
  return 'en';
};

const Step1Username = ({ userData, onComplete }) => {
  const { t, i18n } = useTranslation('common');
  const [formData, setFormData] = useState({
    username: '',
    language: '',
    country_code: FEATURES.DEFAULT_COUNTRY // Default to LV
  });
  const [favoriteLocation, setFavoriteLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUsernameValid, setIsUsernameValid] = useState(true);
  const [displayUsername, setDisplayUsername] = useState(''); // For display
  const [usernameError, setUsernameError] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [languageAutoDetected, setLanguageAutoDetected] = useState(() => {
    // Check if user has already manually selected a language in this session
    return sessionStorage.getItem('userSelectedLanguage') === 'true';
  }); // Track if language was auto-detected

  // Username validation function
  const validateUsername = (username) => {
    if (!username) {
      return { isValid: false, error: t('errors.username_required') };
    }
    
    if (username.length < USERNAME_MIN_LENGTH) {
      return { isValid: false, error: t('errors.username_too_short', { min: USERNAME_MIN_LENGTH }) };
    }
    
    if (username.length > USERNAME_MAX_LENGTH) {
      return { isValid: false, error: t('errors.username_too_long', { max: USERNAME_MAX_LENGTH }) };
    }
    
    if (!USERNAME_REGEX.test(username)) {
      return { isValid: false, error: t('errors.username_invalid_characters') };
    }
    
    // Check for reserved words
    const reservedWords = ['admin', 'administrator', 'root', 'system', 'support', 'help', 'api', 'null', 'undefined'];
    if (reservedWords.includes(username.toLowerCase())) {
      return { isValid: false, error: t('errors.username_reserved') };
    }
    
    return { isValid: true, error: null };
  };

  // Handler for favorite location changes
  const handleFavoriteLocationChange = useCallback((locationData) => {
    setFavoriteLocation(locationData);
    console.log('📍 Favorite location updated:', locationData);
  }, []);

  // Debounced username check with validation
  const debouncedCheckUsername = useCallback(
    debounce(async (username) => {
      // First validate format locally
      const validation = validateUsername(username);
      
      if (!validation.isValid) {
        setIsUsernameValid(false);
        setUsernameError(validation.error);
        setUsernameLoading(false);
        return;
      }

      try {
        // Check availability in database
        const { data, error } = await supabase
          .from('profiles')
          .select('username')
          .ilike('username', username.toLowerCase())
          .neq('id', userData.id);

        if (error) throw error;

        // If we found any matches, username is taken
        if (data && data.length > 0) {
          setIsUsernameValid(false);
          setUsernameError(t('errors.username_taken'));
        } else {
          setIsUsernameValid(true);
          setUsernameError('');
        }
      } catch (error) {
        console.error('Error checking username:', error);
        logProfileError(error, 'step1', { action: 'username_validation', username: username.substring(0, 3) + '***' });
        setIsUsernameValid(false);
        setUsernameError(t('errors.username_check_error'));
      } finally {
        setUsernameLoading(false);
      }
    }, 500),
    [userData.id, t] // Removed validateUsername from dependencies
  );

  // Initialize form data with user's manual language selection if available
  useEffect(() => {
    const userSelectedLanguage = sessionStorage.getItem('selectedLanguage');
    const hasUserSelectedLanguage = sessionStorage.getItem('userSelectedLanguage') === 'true';
    
    if (hasUserSelectedLanguage && userSelectedLanguage) {
      console.log('🌐 Restoring user selected language:', userSelectedLanguage);
      setFormData(prev => ({
        ...prev,
        language: userSelectedLanguage
      }));
      setLanguageAutoDetected(true);
      
      // Ensure i18n is also set to the user's choice
      if (i18n.language !== userSelectedLanguage) {
        i18n.changeLanguage(userSelectedLanguage);
      }
    }
  }, []); // Run only once on mount

  // Generate suggested username from email - only run once
  useEffect(() => {
    // Only generate username if we don't already have one and we have user email
    if (userData?.email && !formData.username && !displayUsername && isInitialLoad) {
      const generateUsername = async () => {
        try {
          setLoading(true);
          console.log('Generating username for email:', userData.email);
          
          // Call RPC to generate unique username
          const { data, error } = await supabase
            .rpc('generate_unique_username', { 
              email: userData.email 
            });

          if (error) {
            console.error('RPC username generation error:', error);
            throw error;
          }

          if (!data) {
            throw new Error('No username returned from generator');
          }

          const suggestedUsername = data.toLowerCase();
          console.log('Generated username:', suggestedUsername);
          
          setFormData(prev => ({
            ...prev,
            username: suggestedUsername
          }));
          setDisplayUsername(suggestedUsername);
          setIsInitialLoad(false); // Mark as no longer initial load
          
          // Validate the generated username
          debouncedCheckUsername.cancel();
          debouncedCheckUsername(suggestedUsername);
        } catch (error) {
          console.error('Error generating username:', error);
          logProfileError(error, 'step1', { action: 'username_generation', email: userData.email?.substring(0, 5) + '***' });
          
          // Fallback to email prefix if RPC fails
          if (userData.email) {
            console.log('Using fallback username generation');
            const emailPrefix = userData.email.split('@')[0]
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '') // Remove special characters
              .slice(0, 8); // Limit length for 10 char max
            
            const fallbackUsername = emailPrefix + Math.floor(Math.random() * 99 + 10); // 2-digit number
            
            setFormData(prev => ({
              ...prev,
              username: fallbackUsername
            }));
            setDisplayUsername(fallbackUsername);
            setIsInitialLoad(false); // Mark as no longer initial load
            
            // Check this fallback username
            debouncedCheckUsername(fallbackUsername);
            console.log('Fallback username set:', fallbackUsername);
          } else {
            setError(t('profile_setup.error_generate_username'));
          }
        } finally {
          setLoading(false);
        }
      };

      generateUsername();
    }
    
    // Auto-detect language from browser with fallback to EN (only once and only if user hasn't manually selected)
    if (!formData.language && !languageAutoDetected && !sessionStorage.getItem('userSelectedLanguage')) {
      const detectedLanguage = detectUserLanguage();
      const targetLanguage = userData?.profile?.language || detectedLanguage;
      
      setFormData(prev => ({
        ...prev,
        language: targetLanguage
      }));
      
      // Update i18n but don't trigger page refresh
      if (i18n.language !== targetLanguage) {
        i18n.changeLanguage(targetLanguage);
      }
      
      // Mark as auto-detected so it doesn't run again
      setLanguageAutoDetected(true);
      console.log('🌐 Language auto-detected:', targetLanguage);
    } else if (userData?.profile?.language && !sessionStorage.getItem('userSelectedLanguage')) {
      // If user has a saved language preference in profile but hasn't manually selected in this session
      const profileLanguage = userData.profile.language;
      setFormData(prev => ({
        ...prev,
        language: profileLanguage
      }));
      
      if (i18n.language !== profileLanguage) {
        i18n.changeLanguage(profileLanguage);
      }
      
      setLanguageAutoDetected(true);
      console.log('🌐 Language loaded from profile:', profileLanguage);
    }
  }, [userData?.email, userData?.profile?.language, languageAutoDetected]); // Added languageAutoDetected dependency

  // Cleanup effect to cancel debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedCheckUsername.cancel();
    };
  }, [debouncedCheckUsername]);

  // Cleanup effect - clear session storage when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if user is navigating away from profile completion
      // This prevents clearing when just switching between steps
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/complete-profile')) {
        sessionStorage.removeItem('userSelectedLanguage');
        sessionStorage.removeItem('selectedLanguage');
      }
    };
  }, []);

  // Handle language change
  const handleLanguageChange = async (e) => {
    const selectedLanguage = e.target.value;
    
    console.log('🌐 User manually changed language to:', selectedLanguage);
    
    try {
      // Mark that user has manually selected a language (persists across refreshes)
      sessionStorage.setItem('userSelectedLanguage', 'true');
      sessionStorage.setItem('selectedLanguage', selectedLanguage);
      
      // Update form data immediately
      setFormData(prev => ({
        ...prev,
        language: selectedLanguage
      }));
      
      // Mark as manually selected
      setLanguageAutoDetected(true);
      
      // Change i18n language without causing page refresh
      await i18n.changeLanguage(selectedLanguage);
      
      console.log('✅ Language changed successfully to:', selectedLanguage);
    } catch (error) {
      console.error('❌ Error changing language:', error);
      // If language change fails, revert the selection
      setFormData(prev => ({
        ...prev,
        language: i18n.language // Revert to current i18n language
      }));
    }
  };

  // Handle username change with input sanitization
  const handleUsernameChange = (e) => {
    let value = e.target.value;
    
    // Sanitize input: remove invalid characters and limit length
    value = value.replace(/[^a-zA-Z0-9_]/g, ''); // Only allow alphanumeric and underscore
    value = value.slice(0, USERNAME_MAX_LENGTH); // Limit length
    
    setUsernameLoading(true);
    setIsInitialLoad(false); // Mark as user interaction
    
    // Update display value (sanitized)
    setDisplayUsername(value);
    
    // Store lowercase version in formData
    setFormData(prev => ({ 
      ...prev, 
      username: value.toLowerCase()
    }));
    
    // Clear previous validation state
    setIsUsernameValid(true);
    setUsernameError('');
    
    // Check availability with debounce (only if not empty)
    debouncedCheckUsername.cancel();
    if (value.trim()) {
      debouncedCheckUsername(value.toLowerCase());
    } else {
      setUsernameLoading(false);
    }
  };

  // Handle form submission with comprehensive validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const startTime = Date.now();
    logProfileAction('step1_submit_attempt', 'step1', { 
      hasUsername: !!formData.username,
      hasCity: !!formData.city,
      language: formData.language 
    });
    
    // Comprehensive validation before submission
    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.isValid) {
      setError(usernameValidation.error);
      setIsUsernameValid(false);
      setUsernameError(usernameValidation.error);
      return;
    }

    if (usernameLoading) {
      setError(t('errors.username_check_in_progress'));
      return;
    }

    if (!isUsernameValid) {
      setError(usernameError || t('errors.username_invalid'));
      return;
    }

    // Validate language selection
    if (!formData.language) {
      setError(t('errors.language_required'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Final username availability check
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .ilike('username', formData.username)
        .neq('id', userData.id);

      if (error) throw error;

      if (data && data.length > 0) {
        setError(t('errors.username_taken'));
        setIsUsernameValid(false);
        return;
      }

      // Sanitize and prepare data for submission
      const sanitizedData = {
        username: formData.username.toLowerCase().trim(),
        language: formData.language,
        country_code: formData.country_code // Include country code
      };

      // Save favorite location if provided
      if (favoriteLocation && favoriteLocation.address && favoriteLocation.latitude) {
        try {
          console.log('💾 Saving favorite location to database...');

          const locationData = {
            user_id: userData.id,
            label: favoriteLocation.label || 'Home',
            address: favoriteLocation.address,
            city: favoriteLocation.city || null,
            latitude: favoriteLocation.latitude,
            longitude: favoriteLocation.longitude,
            place_id: favoriteLocation.place_id || null,
            radius_km: favoriteLocation.radius_km,
            notify_for_rides: favoriteLocation.notify_for_rides,
            notify_for_packages: favoriteLocation.notify_for_packages,
            match_start_point: favoriteLocation.match_start_point,
            match_end_point: favoriteLocation.match_end_point,
            is_active: true
          };

          const { error: locationError } = await supabase
            .from('user_favorite_locations')
            .insert(locationData);

          if (locationError) {
            console.error('Error saving favorite location:', locationError);
            // Don't fail the whole process if favorite location save fails
            logProfileError(locationError, 'step1', { action: 'save_favorite_location' });
          } else {
            console.log('✅ Favorite location saved successfully');
            logProfileAction('favorite_location_saved', 'step1', { hasLabel: !!favoriteLocation.label });
          }
        } catch (locationError) {
          console.error('Error in favorite location save:', locationError);
          // Continue with profile completion even if location save fails
        }
      }

      // Submit the data to parent component
      await onComplete(sanitizedData);
      
      // Clear temporary language selection flags since profile setup is complete
      sessionStorage.removeItem('userSelectedLanguage');
      sessionStorage.removeItem('selectedLanguage');
      
      // Log successful completion
      const completionTime = Date.now() - startTime;
      logProfilePerformance('step1_completion_time', completionTime, 'step1');
      logProfileAction('step1_completed', 'step1', {
        completionTime,
        hasFavoriteLocation: !!(favoriteLocation && favoriteLocation.address)
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      logProfileError(error, 'step1', {
        action: 'form_submission',
        hasUsername: !!formData.username,
        hasFavoriteLocation: !!(favoriteLocation && favoriteLocation.address)
      });
      setError(error.message || t('errors.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="pcf-form-step">
      <div className="pcf-content-container">
        <div className="pcf-form-field">
          <label>{t('profile_setup.language')} <span className="pcf-required">*</span></label>
          <select
            className="pcf-form-select"
            value={formData.language}
            onChange={handleLanguageChange}
            disabled={loading}
            required
          >
            <option value="en">{t('languages.english')}</option>
            <option value="lv">{t('languages.latvian')}</option>
            <option value="ru">{t('languages.russian')}</option>
          </select>
        </div>

        <div className="pcf-form-field">
          <label>{t('profile_setup.public_name')} <span className="pcf-required">*</span></label>
          <div className="pcf-username-input-container">
            <input
              className="enhanced-profile-input"
              value={displayUsername}
              onChange={handleUsernameChange}
              placeholder={t('profile_setup.username_placeholder')}
              disabled={loading}
              maxLength={USERNAME_MAX_LENGTH}
              required={true}
            />
            {usernameLoading && (
              <div className="pcf-username-loading">
                <Spinner size="small" showText={false} thickness={2} />
              </div>
            )}
            {autoSaveStatus !== 'idle' && (
              <div className={`pcf-auto-save-indicator ${autoSaveStatus} ${autoSaveStatus !== 'idle' ? 'show' : ''}`}>
                {autoSaveStatus === 'saving' && '💾'}
                {autoSaveStatus === 'saved' && '✓'}
                {autoSaveStatus === 'error' && '⚠'}
              </div>
            )}
          </div>
          <div className="pcf-username-feedback">
            <small className="pcf-helper-text">
              {t('profile_setup.username_requirements', { 
                min: USERNAME_MIN_LENGTH, 
                max: USERNAME_MAX_LENGTH 
              })}
            </small>
          </div>
          {(usernameError || !isUsernameValid) && (
            <span className="pcf-error-text">
              {usernameError || t('errors.username_invalid')}
            </span>
          )}
          {isUsernameValid && formData.username && !usernameLoading && (
            <span className="pcf-success-text">
              ✓ {t('profile_setup.username_available')}
            </span>
          )}
        </div>

        <div className="pcf-form-field">
          <label>{t('profile_setup.country')} <span className="pcf-required">*</span></label>
          <select
            className="pcf-form-select"
            value={formData.country_code}
            onChange={(e) => {
              const selectedCountry = e.target.value;
              console.log('🌍 User selected country:', selectedCountry);
              setFormData(prev => ({
                ...prev,
                country_code: selectedCountry
              }));
            }}
            disabled={loading}
            required
          >
            {getCountryOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <small className="pcf-helper-text">
            {t('profile_setup.country_help')}
          </small>
        </div>

        {/* Favorite Location - Optional */}
        <AddFavoriteLocationInline
          onLocationChange={handleFavoriteLocationChange}
          initialLocation={favoriteLocation}
          disabled={loading}
          showLabel={true}
        />

        {error && <div className="pcf-error-text">{error}</div>}

        <div className="pcf-button-group">
          <button
            type="submit"
            className="enhanced-profile-button primary"
            disabled={loading || usernameLoading || !isUsernameValid || !formData.username}
          >
            {loading ? (
              <div className="button-label-with-spinner">
                <Spinner size="small" color="var(--background-color)" showText={false} />
                <span>{t('app.loading')}</span>
              </div>
            ) : (
              t('buttons.next')
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step1Username;